// Lightweight Facebook Pixel helper + dev/debug mode.
//
// Enable debug mode by either:
//   • Adding `?fbDebug=1` to any URL (persists for the session)
//   • Running `localStorage.setItem('fbDebug','1')` in DevTools
// Disable with `?fbDebug=0` or `localStorage.removeItem('fbDebug')`.
//
// In debug mode, every fbq() call we make is mirrored to the console with a
// `[FB Pixel]` prefix. The base pixel (PageView on load) is installed in
// index.html — this module adds route-aware tracking on top.

const PIXEL_ID = "628827664252860";
const STORAGE_KEY = "fbDebug";

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean };
    _fbq?: unknown;
  }
}

const readDebugFlag = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    const param = url.searchParams.get("fbDebug");
    if (param === "1") {
      localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    if (param === "0") {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

export const isPixelDebug = (): boolean => readDebugFlag();

const log = (...args: unknown[]) => {
  if (!isPixelDebug()) return;
  // eslint-disable-next-line no-console
  console.info("%c[FB Pixel]", "color:#1877F2;font-weight:bold;", ...args);
};

const waitForFbq = (timeoutMs = 4000): Promise<boolean> =>
  new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        return resolve(true);
      }
      if (Date.now() - start > timeoutMs) return resolve(false);
      window.setTimeout(check, 100);
    };
    check();
  });

const safeFbq = async (...args: unknown[]) => {
  const ready = await waitForFbq();
  if (!ready || typeof window.fbq !== "function") {
    log("fbq not available, dropping call:", args);
    return;
  }
  window.fbq(...args);
  log("fired →", args);
};

/** Standard event tracking (e.g. PageView, ViewContent, Lead, Contact). */
export const trackPixel = (
  event: string,
  params?: Record<string, unknown>,
) => {
  if (params) {
    void safeFbq("track", event, params);
  } else {
    void safeFbq("track", event);
  }
};

/** Custom event tracking. */
export const trackPixelCustom = (
  event: string,
  params?: Record<string, unknown>,
) => {
  void safeFbq("trackCustom", event, params ?? {});
};

/** Confirm at startup that the pixel script + ID resolved correctly. */
export const verifyPixelInstalled = () => {
  if (!isPixelDebug()) return;
  void waitForFbq().then((ready) => {
    if (!ready) {
      // eslint-disable-next-line no-console
      console.warn("[FB Pixel] fbq() never appeared on window — pixel script blocked or missing.");
      return;
    }
    log(`installed (id ${PIXEL_ID}). Open Meta Pixel Helper or Events Manager → Test Events to verify hits.`);
  });
};

export const PIXEL_DEBUG_HINT =
  "Append ?fbDebug=1 to any URL to enable Facebook Pixel debug logs.";
